import { ArrowLeft, ChevronRight, Info } from "lucide-react";
import { useState } from "react";
import { formatCurrency } from "@/components/deal-metrics/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { DrillableValue, type DrillDownRequest } from "./drillable-value";

interface QuarterData {
  label: string;
  closedDeals: number;
  earned: number;
}

interface Referral {
  dealId: string;
  customer: string;
  productType: string | null;
  quoteDate: string | null;
  orderDate: string | null;
  gross: number | null;
  ownerName: string | null;
  status: "counted" | "not_production" | "returned" | "open" | "other_year";
  quarter: string | null;
}

interface AffiliateRow {
  affiliateName: string;
  closedDeals: number;
  bonusPerDeal: number;
  totalEarned: number;
  quarters: QuarterData[];
  referrals?: Referral[];
}

interface AffiliatePerformanceData {
  affiliateBonusPerDeal: number;
  rows: AffiliateRow[];
}

interface DRAffiliateRow {
  affiliateName: string;
  closedDeals: number;
  totalEarned: number;
  quarters: QuarterData[];
}

interface DRConsultant {
  name: string;
  hubspotOwnerId: string;
  affiliates: DRAffiliateRow[];
  totalDeals: number;
  totalEarned: number;
}

export interface DRAffiliatePerformanceData {
  drBonusPerDeal: number;
  consultants: DRConsultant[];
}

export function AffiliatePerformanceSection({ data }: { data: AffiliatePerformanceData }) {
  const quarterLabels = data.rows[0]?.quarters.map((q) => q.label) ?? ["Q1", "Q2", "Q3", "Q4"];

  // Aggregate per quarter across all affiliates
  const quarterAggregates = quarterLabels.map((_, qi) => {
    const deals = data.rows.reduce((s, r) => s + (r.quarters[qi]?.closedDeals ?? 0), 0);
    const earned = data.rows.reduce((s, r) => s + (r.quarters[qi]?.earned ?? 0), 0);
    return { deals, earned };
  });

  const grandTotalDeals = data.rows.reduce((s, r) => s + r.closedDeals, 0);
  const grandTotalEarned = data.rows.reduce((s, r) => s + r.totalEarned, 0);

  return (
    <div className="space-y-4">
      <div className="relative py-2">
        <Separator />
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-4 text-xs font-medium uppercase tracking-widest text-muted-foreground">
          Affiliate Coaching
        </span>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {/* Config card */}
        <Card className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-accent/[0.04] to-transparent pointer-events-none" />
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-1.5 text-sm font-medium uppercase tracking-wider text-muted-foreground">
              Affiliate Coaching
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3 w-3 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs">
                    <p>
                      Every closed deal with a coached affiliate's name counts — whoever the
                      consultant on the deal is.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-3">
              {data.affiliateBonusPerDeal > 0 && (
                <div>
                  <dt className="text-xs text-muted-foreground">Bonus/Deal</dt>
                  <dd className="text-lg font-bold tabular-nums">
                    {formatCurrency(data.affiliateBonusPerDeal)}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-xs text-muted-foreground">Affiliates</dt>
                <dd className="text-lg font-bold tabular-nums">{data.rows.length}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">YTD Deals</dt>
                <dd className="text-lg font-bold tabular-nums">{grandTotalDeals}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">YTD Earned</dt>
                <dd className="text-lg font-bold tabular-nums">
                  {formatCurrency(grandTotalEarned)}
                </dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        {/* Quarter cards */}
        {quarterLabels.map((label, qi) => {
          const agg = quarterAggregates[qi]!;
          const hasPayout = agg.earned > 0;

          return (
            <Card
              key={label}
              className={cn(
                "relative overflow-hidden transition-shadow",
                hasPayout && "ring-1 ring-accent/30 shadow-md",
              )}
            >
              {hasPayout && (
                <div className="absolute inset-0 bg-gradient-to-br from-accent/[0.06] to-transparent pointer-events-none" />
              )}
              <CardHeader className="pb-2">
                <CardTitle className="text-center font-heading text-xl tracking-tight">
                  {label}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="space-y-1.5 text-sm">
                  {data.rows.map((row) => {
                    const q = row.quarters[qi]!;
                    return (
                      <div key={row.affiliateName} className="flex justify-between">
                        <dt className="text-muted-foreground truncate mr-2">{row.affiliateName}</dt>
                        <dd className="font-semibold tabular-nums shrink-0">
                          {q.closedDeals} {q.closedDeals === 1 ? "deal" : "deals"}
                        </dd>
                      </div>
                    );
                  })}
                  <Separator className="my-1" />
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground font-medium">Total Deals</dt>
                    <dd className="font-bold tabular-nums">{agg.deals}</dd>
                  </div>
                  <Separator className="my-1" />
                  <div className="flex justify-between items-center">
                    <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Payout
                    </dt>
                    <dd
                      className={cn(
                        "text-lg font-bold tabular-nums tracking-tight",
                        hasPayout ? "text-accent-foreground" : "text-muted-foreground",
                      )}
                    >
                      {formatCurrency(agg.earned)}
                    </dd>
                  </div>
                </dl>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {data.rows.some((r) => r.referrals) && <AffiliateReferralList rows={data.rows} />}
    </div>
  );
}

export function DRAffiliatePerformanceSection({
  data,
  onDrillDown,
}: {
  data: DRAffiliatePerformanceData;
  onDrillDown: (req: DrillDownRequest) => void;
}) {
  const quarterLabels = ["Q1", "Q2", "Q3", "Q4"];
  // Per-card drill-down state: null = overview, string = consultant hubspotOwnerId
  const [drillDown, setDrillDown] = useState<Record<number, string | null>>({});

  // Aggregate across all consultants per quarter
  const quarterAggregates = quarterLabels.map((_, qi) => {
    let deals = 0;
    let earned = 0;
    for (const c of data.consultants) {
      for (const a of c.affiliates) {
        deals += a.quarters[qi]?.closedDeals ?? 0;
        earned += a.quarters[qi]?.earned ?? 0;
      }
    }
    return { deals, earned };
  });

  const grandTotalDeals = data.consultants.reduce((s, c) => s + c.totalDeals, 0);
  const grandTotalEarned = data.consultants.reduce((s, c) => s + c.totalEarned, 0);

  return (
    <div className="space-y-4">
      <div className="relative py-2">
        <Separator />
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-4 text-xs font-medium uppercase tracking-widest text-muted-foreground">
          Direct Reports — Affiliate Coaching
        </span>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {/* Config card */}
        <Card className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-accent/[0.04] to-transparent pointer-events-none" />
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-1.5 text-sm font-medium uppercase tracking-wider text-muted-foreground">
              DR Affiliate Coaching
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3 w-3 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-xs">
                    <p>
                      Bonus earned from direct reports' affiliate deals. Each consultant's mapped
                      affiliates are tracked independently.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-3">
              {data.drBonusPerDeal > 0 && (
                <div>
                  <dt className="text-xs text-muted-foreground">DR Bonus/Deal</dt>
                  <dd className="text-lg font-bold tabular-nums">
                    {formatCurrency(data.drBonusPerDeal)}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-xs text-muted-foreground">Consultants</dt>
                <dd className="text-lg font-bold tabular-nums">{data.consultants.length}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">YTD Deals</dt>
                <dd className="text-lg font-bold tabular-nums">{grandTotalDeals}</dd>
              </div>
              <div>
                <dt className="text-xs text-muted-foreground">YTD Earned</dt>
                <dd className="text-lg font-bold tabular-nums">
                  {formatCurrency(grandTotalEarned)}
                </dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        {/* Quarter cards */}
        {quarterLabels.map((label, qi) => {
          const agg = quarterAggregates[qi]!;
          const hasPayout = agg.earned > 0;
          const drilledId = drillDown[qi] ?? null;
          const drilledConsultant = drilledId
            ? data.consultants.find((c) => c.hubspotOwnerId === drilledId)
            : null;

          return (
            <Card
              key={label}
              className={cn(
                "relative overflow-hidden transition-shadow",
                hasPayout && "ring-1 ring-accent/30 shadow-md",
              )}
            >
              {hasPayout && (
                <div className="absolute inset-0 bg-gradient-to-br from-accent/[0.06] to-transparent pointer-events-none" />
              )}
              <CardHeader className="pb-2">
                <CardTitle className="text-center font-heading text-xl tracking-tight">
                  {label}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {drilledConsultant ? (
                  /* ── Drilled-in: single consultant's affiliate breakdown ── */
                  <dl className="space-y-1.5 text-sm">
                    <button
                      type="button"
                      onClick={() => setDrillDown((prev) => ({ ...prev, [qi]: null }))}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mb-1 cursor-pointer"
                    >
                      <ArrowLeft className="h-3 w-3" />
                      All consultants
                    </button>
                    <div className="font-medium text-sm border-b pb-1.5 mb-1">
                      {drilledConsultant.name}
                    </div>
                    {drilledConsultant.affiliates.map((aff) => {
                      const affQ = aff.quarters[qi]!;
                      return (
                        <div key={aff.affiliateName} className="flex justify-between">
                          <dt className="text-muted-foreground truncate mr-2">
                            {aff.affiliateName}
                          </dt>
                          <dd className="font-semibold tabular-nums shrink-0">
                            <DrillableValue
                              onClick={() =>
                                onDrillDown({
                                  metric: "closedDeals",
                                  section: "dr-aff",
                                  quarter: label,
                                  drOwnerId: drilledConsultant.hubspotOwnerId,
                                  affiliateNames: [aff.affiliateName],
                                  label: `${aff.affiliateName} — ${drilledConsultant.name} — ${label}`,
                                })
                              }
                            >
                              {affQ.closedDeals} {affQ.closedDeals === 1 ? "deal" : "deals"}
                            </DrillableValue>
                          </dd>
                        </div>
                      );
                    })}
                    <Separator className="my-1" />
                    {(() => {
                      const cDeals = drilledConsultant.affiliates.reduce(
                        (s, a) => s + (a.quarters[qi]?.closedDeals ?? 0),
                        0,
                      );
                      return (
                        <>
                          <div className="flex justify-between">
                            <dt className="text-muted-foreground font-medium">Total Deals</dt>
                            <dd className="font-bold tabular-nums">
                              <DrillableValue
                                onClick={() =>
                                  onDrillDown({
                                    metric: "closedDeals",
                                    section: "dr-aff",
                                    quarter: label,
                                    drOwnerId: drilledConsultant.hubspotOwnerId,
                                    affiliateNames: drilledConsultant.affiliates.map(
                                      (a) => a.affiliateName,
                                    ),
                                    label: `All Affiliates — ${drilledConsultant.name} — ${label}`,
                                  })
                                }
                              >
                                {cDeals}
                              </DrillableValue>
                            </dd>
                          </div>
                          <Separator className="my-1" />
                          <div className="flex justify-between items-center">
                            <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                              Earned
                            </dt>
                            <dd
                              className={cn(
                                "text-lg font-bold tabular-nums tracking-tight",
                                cDeals > 0 ? "text-accent-foreground" : "text-muted-foreground",
                              )}
                            >
                              {formatCurrency(cDeals * data.drBonusPerDeal)}
                            </dd>
                          </div>
                        </>
                      );
                    })()}
                  </dl>
                ) : (
                  /* ── Overview: all consultants ── */
                  <dl className="space-y-1.5 text-sm">
                    {data.consultants.map((consultant) => {
                      const consultantQDeals = consultant.affiliates.reduce(
                        (s, a) => s + (a.quarters[qi]?.closedDeals ?? 0),
                        0,
                      );
                      return (
                        <button
                          type="button"
                          key={consultant.hubspotOwnerId}
                          onClick={() =>
                            setDrillDown((prev) => ({
                              ...prev,
                              [qi]: consultant.hubspotOwnerId,
                            }))
                          }
                          className="flex justify-between items-center w-full rounded-sm px-1 -mx-1 py-0.5 hover:bg-muted/60 transition-colors cursor-pointer group"
                        >
                          <span className="font-medium truncate mr-2 flex items-center gap-1">
                            {consultant.name}
                            <ChevronRight className="h-3 w-3 text-muted-foreground/0 group-hover:text-muted-foreground transition-colors" />
                          </span>
                          <span
                            className={cn(
                              "tabular-nums shrink-0",
                              consultantQDeals > 0 ? "font-semibold" : "text-muted-foreground",
                            )}
                          >
                            {consultantQDeals} {consultantQDeals === 1 ? "deal" : "deals"}
                          </span>
                        </button>
                      );
                    })}
                    <Separator className="my-1" />
                    <div className="flex justify-between">
                      <dt className="text-muted-foreground font-medium">Total Deals</dt>
                      <dd className="font-bold tabular-nums">
                        <DrillableValue
                          onClick={() => {
                            const allAffNames = data.consultants.flatMap((c) =>
                              c.affiliates.map((a) => a.affiliateName),
                            );
                            const allDrOwnerIds = data.consultants
                              .map((c) => c.hubspotOwnerId)
                              .filter(Boolean);
                            onDrillDown({
                              metric: "closedDeals",
                              section: "dr-aff",
                              quarter: label,
                              drOwnerId: allDrOwnerIds.join(","),
                              affiliateNames: [...new Set(allAffNames)],
                              label: `All DR Affiliate Deals — ${label}`,
                            });
                          }}
                        >
                          {agg.deals}
                        </DrillableValue>
                      </dd>
                    </div>
                    <Separator className="my-1" />
                    <div className="flex justify-between items-center">
                      <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        Payout
                      </dt>
                      <dd
                        className={cn(
                          "text-lg font-bold tabular-nums tracking-tight",
                          hasPayout ? "text-accent-foreground" : "text-muted-foreground",
                        )}
                      >
                        {formatCurrency(agg.earned)}
                      </dd>
                    </div>
                  </dl>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ── Referral list (cross-check) ──────────────────────────────────────

const fmtDate = (d: string | null) => {
  if (!d) return "—";
  const [y, m, day] = d.slice(0, 10).split("-").map(Number) as [number, number, number];
  return new Date(y, m - 1, day).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "2-digit",
  });
};

function statusBadge(r: Referral) {
  const base = "rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap";
  switch (r.status) {
    case "counted":
      return (
        <span className={cn(base, "bg-emerald-500/10 text-emerald-700")}>
          Counted{r.quarter ? ` · ${r.quarter}` : ""}
        </span>
      );
    case "not_production":
      return (
        <span className={cn(base, "bg-amber-500/10 text-amber-700")}>
          Not counted — not in Production pipeline
        </span>
      );
    case "returned":
      return <span className={cn(base, "bg-rose-500/10 text-rose-700")}>Returned / refunded</span>;
    case "other_year":
      return (
        <span className={cn(base, "bg-muted text-muted-foreground")}>Ordered in another FY</span>
      );
    default:
      return <span className={cn(base, "bg-sky-500/10 text-sky-700")}>Open — not ordered yet</span>;
  }
}

/**
 * Everyone referred by each coached affiliate this fiscal year, so the bonus
 * count can be cross-checked deal by deal (and deals sitting under another
 * consultant, or not yet ordered, are easy to spot).
 */
function AffiliateReferralList({ rows }: { rows: AffiliateRow[] }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Referred by each affiliate</CardTitle>
        <p className="text-xs text-muted-foreground">
          Every deal this fiscal year with the affiliate's name on it in HubSpot. "Counted" deals
          earn the bonus: ordered this fiscal year and not returned — whoever closed the deal.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {rows.map((row) => {
          const refs = row.referrals ?? [];
          const counted = refs.filter((r) => r.status === "counted").length;
          return (
            <div key={row.affiliateName}>
              <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <p className="font-heading text-lg">{row.affiliateName}</p>
                <p className="text-xs text-muted-foreground">
                  {refs.length} referred · {counted} counted
                </p>
              </div>
              {refs.length === 0 ? (
                <p className="rounded-md border border-dashed px-3 py-3 text-sm text-muted-foreground">
                  No deals with this affiliate name yet this fiscal year.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead className="border-b bg-muted/40 text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Customer</th>
                        <th className="px-3 py-2 text-left font-medium">Product</th>
                        <th className="px-3 py-2 text-left font-medium">Quote</th>
                        <th className="px-3 py-2 text-left font-medium">Ordered</th>
                        <th className="px-3 py-2 text-right font-medium">Gross</th>
                        <th className="px-3 py-2 text-left font-medium">Closed by</th>
                        <th className="px-3 py-2 text-left font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {refs.map((r) => (
                        <tr key={r.dealId} className="border-b last:border-0">
                          <td className="px-3 py-2 font-medium">{r.customer}</td>
                          <td className="px-3 py-2 text-muted-foreground">
                            {r.productType ?? "—"}
                          </td>
                          <td className="px-3 py-2 tabular-nums">{fmtDate(r.quoteDate)}</td>
                          <td className="px-3 py-2 tabular-nums">{fmtDate(r.orderDate)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {r.gross != null ? formatCurrency(r.gross) : "—"}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">{r.ownerName ?? "—"}</td>
                          <td className="px-3 py-2">{statusBadge(r)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
